import React, { useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { ThemeContext } from '../context/ThemeContext';
import menuConfig from '../config/menuConfig';
import { useTranslation } from 'react-i18next';
import { HiOutlineGlobe, HiOutlineSun, HiOutlineMoon } from 'react-icons/hi';

const Navbar = () => {
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef(null);

  const path = location.pathname;

  let basePath = 'default';
  if (path === '/') {
    basePath = '/';
  } else {
    const specificPaths = Object.keys(menuConfig)
      .filter((key) => key !== '/' && key !== 'default')
      .sort((a, b) => b.length - a.length);
    const match = specificPaths.find((key) => path.startsWith(key));
    if (match) basePath = match;
  }

  const menuItems = menuConfig[basePath];

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 py-4 px-4 md:px-10 flex items-center justify-between transition-all duration-300 ${
        scrolled
          ? 'bg-white/30 dark:bg-gray-900/30 shadow-sm backdrop-blur-md backdrop-saturate-150'
          : 'bg-transparent'
      }`}
    >
      <NavLink to="/" className="flex items-center space-x-2">
        <Logo isDark={isDarkMode} className="h-8 w-auto" />
      </NavLink>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center gap-4 text-sm">
        {menuItems.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className="hover:text-primaryAccent transition text-gray-700 dark:text-gray-200"
          >
            {t(item.label)}
          </a>
        ))}

        {/* Language Switcher (Globe Dropdown) */}
        <div className="relative" ref={langRef}>
          <button
            onClick={() => setIsLangOpen(!isLangOpen)}
            className="text-xl bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            aria-label="Toggle Language"
          >
            <HiOutlineGlobe />
          </button>
          {isLangOpen && (
            <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded shadow z-50 w-40">
              <button
                onClick={() => {
                  i18n.changeLanguage('id');
                  setIsLangOpen(false);
                }}
                className={`block w-full text-left px-4 py-2 text-sm ${
                  i18n.language === 'id'
                    ? 'bg-primaryAccent text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                🇮🇩 Bahasa Indonesia
              </button>
              <button
                onClick={() => {
                  i18n.changeLanguage('en');
                  setIsLangOpen(false);
                }}
                className={`block w-full text-left px-4 py-2 text-sm ${
                  i18n.language === 'en'
                    ? 'bg-primaryAccent text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                🇺🇸 English
              </button>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          aria-label="Toggle Dark Mode"
          className="text-xl bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition"
        >
          {isDarkMode ? <HiOutlineSun /> : <HiOutlineMoon />}
        </button>
      </div>

      {/* Mobile Menu Button */}
      <div className="md:hidden flex items-center gap-2">
        <button
          onClick={toggleTheme}
          aria-label="Toggle Dark Mode"
          className="text-xl bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition"
        >
          {isDarkMode ? <HiOutlineSun /> : <HiOutlineMoon />}
        </button>

        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="text-2xl text-gray-800 dark:text-white"
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-white dark:bg-gray-900 shadow-md md:hidden text-sm text-gray-700 dark:text-gray-200 px-4 pt-4 pb-6 space-y-3">
          {menuItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="block hover:text-primaryAccent transition"
              onClick={() => setIsMenuOpen(false)}
            >
              {t(item.label)}
            </a>
          ))}

          {/* Language Toggle (Mobile) */}
          <div className="flex flex-col gap-1 pt-4">
            <button
              onClick={() => {
                i18n.changeLanguage('id');
                setIsMenuOpen(false);
              }}
              className={`text-left px-3 py-2 rounded text-sm ${
                i18n.language === 'id'
                  ? 'bg-primaryAccent text-white'
                  : 'hover:text-primaryAccent'
              }`}
            >
              🇮🇩 Bahasa Indonesia
            </button>
            <button
              onClick={() => {
                i18n.changeLanguage('en');
                setIsMenuOpen(false);
              }}
              className={`text-left px-3 py-2 rounded text-sm ${
                i18n.language === 'en'
                  ? 'bg-primaryAccent text-white'
                  : 'hover:text-primaryAccent'
              }`}
            >
              🇺🇸 English
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
